import { useMemo } from "react";
import {
  Info,
  Car,
  Receipt,
  ClipboardList,
  StickyNote,
  Activity,
  CheckCircle2,
  Clock,
  Gauge,
  Plus,
  FileText,
  MessageSquarePlus,
  AlertTriangle,
  MapPin,
  ClipboardCheck,
  CreditCard,
} from "lucide-react";
import "./ClientOverviewTab.css";

const seededValue = (seed, min, max) => {
  const x = Math.sin((seed || 1) * 999) * 10000;
  const frac = x - Math.floor(x);
  return Math.floor(frac * (max - min + 1)) + min;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const formatMoney = (value) =>
  `${(Number(value) || 0).toLocaleString("en-US")} EGP`;

function StaticDot() {
  return (
    <span
      className="cov-sample-dot"
      title="Static — not returned by the API yet"
    />
  );
}

function InfoRow({ label, value, isStatic }) {
  return (
    <div className="cov-info-row">
      <span className="cov-info-label">
        {label} {isStatic && <StaticDot />}
      </span>
      <span className="cov-info-value">{value || "—"}</span>
    </div>
  );
}

const ACTIVITY_ITEMS = [
  {
    id: 1,
    icon: ClipboardCheck,
    tone: "teal",
    title: "Order Completed",
    sub: "ESTBN-ORD-240501-00128",
    time: "01 May 2024, 11:45 AM",
  },
  {
    id: 2,
    icon: CreditCard,
    tone: "green",
    title: "Payment Received",
    sub: "Invoice #INV-240501-00056",
    time: "01 May 2024, 11:48 AM",
  },
];

export default function ClientOverviewTab({
  client,
  orders = [],
  requests = [],
  onGoToTab,
}) {
  const recentOrders = useMemo(() => orders.slice(0, 5), [orders]);

  const requestGroups = useMemo(() => {
    const map = {};
    requests.forEach((r) => {
      const label = r.status?.label || "Unknown";
      map[label] = (map[label] || 0) + 1;
    });
    const total = requests.length;
    return Object.entries(map).map(([label, count]) => ({
      label,
      count,
      pct: total ? Math.round((count / total) * 100) : 0,
    }));
  }, [requests]);

  const avgOrderValue =
    client.orders_count > 0
      ? Math.round((client.orders_total_sum || 0) / client.orders_count)
      : null;

  const healthScore = useMemo(() => {
    const base =
      (client.is_active ? 25 : 0) +
      Math.min(client.orders_count || 0, 10) * 6 +
      15;
    return Math.max(5, Math.min(98, base));
  }, [client]);

  const healthLabel =
    healthScore >= 80
      ? "Excellent"
      : healthScore >= 50
        ? "Good"
        : "Needs Attention";

  const riskScore = seededValue(client.id, 2, 95);
  const riskLevel =
    riskScore >= 60 ? "high" : riskScore >= 30 ? "medium" : "low";
  const RISK_LABEL = { low: "Low", medium: "Medium", high: "High" };

  const isRecentlyActive =
    client.user_last_activity?.last_active_human === "Active now";

  const vehicles = client.vehicles || [];

  return (
    <div className="cov-grid">
      {/* ---------------- Column 1 ---------------- */}
      <div>
        <div className="cov-card cov-col-1">
          <div className="cov-card-header">
            <h4>
              <Info size={15} /> Client Information
            </h4>
          </div>
          <InfoRow label="Full Name" value={client.name} />
          <InfoRow label="Date of Birth" value={formatDate(client.birthdate)} />
          <InfoRow label="Gender" value={client.gender} />
          <InfoRow label="Mobile Number" value={client.phone_number} />
          <InfoRow label="Email Address" value={client.email} />
          <InfoRow label="National ID" value="—" isStatic />
          <InfoRow label="Marital Status" value="—" isStatic />
          <InfoRow label="Occupation" value="—" isStatic />
          <InfoRow label="Company" value="—" isStatic />
          <InfoRow label="Source" value="—" isStatic />
          <InfoRow label="Referral Code" value="—" isStatic />
          <InfoRow label="Notes" value="—" isStatic />
          <button className="cov-btn-outline cov-full-btn">
            View Full Profile
          </button>
        </div>
        <div className="cov-card mt-3">
          <div className="cov-card-header">
            <h4>
              <Car size={15} /> Vehicles ({vehicles.length})
            </h4>
            <button
              className="cov-link"
              onClick={() => onGoToTab?.("vehicles")}
            >
              View All ›
            </button>
          </div>
          {vehicles.length === 0 ? (
            <p className="cov-empty">No vehicles yet.</p>
          ) : (
            vehicles.slice(0, 2).map((v) => (
              <div className="cov-vehicle-row" key={v.id}>
                <div className="cov-vehicle-thumb">
                  <Car size={18} />
                </div>
                <div className=" d-flex flex-column gap-1">
                  <p className="cov-vehicle-name">{v.name} </p>
                  {v.is_primary && (
                    <span className="cov-primary-tag">Primary</span>
                  )}
                  <p className="cov-vehicle-meta">
                    Plate: — <StaticDot /> · Mileage: — <StaticDot />
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* ---------------- Column 2 ---------------- */}
      <div className="cov-col-2">
        <div className="cov-card">
          <div className="cov-card-header">
            <h4>
              <ClipboardList size={15} /> Recent Orders
            </h4>
            <button className="cov-link" onClick={() => onGoToTab?.("orders")}>
              View All Orders ›
            </button>
          </div>
          {recentOrders.length === 0 ? (
            <p className="cov-empty">No orders yet.</p>
          ) : (
            <div className="cov-order-list">
              {recentOrders.map((order) => {
                const serviceName =
                  order.pricing_summary?.category_summary?.items?.[0]?.name ||
                  "Service";
                return (
                  <div className="cov-order-row" key={order.id}>
                    <p className="cov-order-title">{order.order_number}</p>
                    <p className="cov-order-sub">{serviceName}</p>

                    <span
                      className="cov-order-status"
                      style={{
                        color: order.status?.color || "#6b7280",
                        background: `${order.status?.color || "#6b7280"}1a`,
                      }}
                    >
                      {order.status?.label || "—"}
                    </span>
                    <span className="cov-order-date">
                      {formatDate(order.ordered_at || order.scheduled_date)}
                    </span>
                    <span className="cov-order-amount">
                      {formatMoney(order.total)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="cov-mini-stats">
            <div className="cov-mini-stat">
              <Clock size={16} />
              <p className="cov-mini-label">Last Order</p>
              <p className="cov-mini-value">
                {formatDate(client.last_ordered_at)}
              </p>
            </div>
            <div className="cov-mini-stat">
              <Receipt size={16} />
              <p className="cov-mini-label">Average Order Value</p>
              <p className="cov-mini-value">
                {avgOrderValue !== null ? formatMoney(avgOrderValue) : "—"}
              </p>
            </div>
            <div className="cov-mini-stat">
              <Activity size={16} />
              <p className="cov-mini-label">
                Order Frequency <StaticDot />
              </p>
              <p className="cov-mini-value">—</p>
            </div>
            <div className="cov-mini-stat">
              <CheckCircle2 size={16} />
              <p className="cov-mini-label">
                On-time Rate <StaticDot />
              </p>
              <p className="cov-mini-value">—</p>
            </div>
          </div>
        </div>
        <div className="cov-row-2">
          <div className="cov-card">
            <div className="cov-card-header">
              <h4 className="my-0">
                <FileText size={15} /> Invoices Summary <StaticDot />
              </h4>
              <button
                className="cov-link my-0"
                onClick={() => onGoToTab?.("invoices")}
              >
                View All ›
              </button>
            </div>
            <div className="cov-kv">
              <span>Total Invoices</span>
              <b>28</b>
            </div>
            <div className="cov-kv">
              <span>Paid</span>
              <div className=" d-flex justify-content-between align-items-center">
                <b>22</b>
                <b className="cov-green">78.6%</b>
              </div>
            </div>
            <div className="cov-kv">
              <span>Outstanding</span>
              <div className=" d-flex justify-content-between align-items-center ">
                <b>4</b>
                <b className="cov-red">14.3%</b>
              </div>
            </div>
            <div className="cov-kv">
              <span>Overdue</span>
              <div className=" d-flex justify-content-between align-items-center">
                <b>2</b>
                <b className="cov-red">7.1%</b>
              </div>
            </div>
            <div className="cov-kv cov-kv-total">
              <span>Total Outstanding</span>
              <b>2,850 EGP</b>
            </div>
          </div>

          <div className="cov-card">
            <div className="cov-card-header">
              <h4>
                <AlertTriangle size={15} /> Requests Overview
              </h4>
              <button
                className="cov-link"
                onClick={() => onGoToTab?.("requests")}
              >
                View Requests ›
              </button>
            </div>
            <div className="cov-kv my-1">
              <span>Total Requests</span>
              <b>{requests.length}</b>
            </div>
            {requestGroups.length === 0 ? (
              <p className="cov-empty">No requests yet.</p>
            ) : (
              requestGroups.map((g) => (
                <div className="cov-kv my-2" key={g.label}>
                  <span>{g.label}</span>
                  <div className=" d-flex justify-content-between align-items-center">
                    <b>{g.count}</b>
                    <b>{g.pct}%</b>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="cov-card">
          <div className="cov-card-header">
            <h4>
              <StickyNote size={15} /> Recent Notes <StaticDot />
            </h4>
          </div>
          <div className="cov-note">
            <p className="cov-note-author">Ahmed Samir (Admin)</p>
            <p className="cov-note-text">
              Client prefers morning appointments between 9 AM - 12 PM.
            </p>
          </div>
        </div>
      </div>

      {/* ---------------- Column 3 ---------------- */}
      <div className="cov-col-3">
        <div className="cov-card">
          <div className="cov-card-header ">
            <h4>
              <Plus size={15} /> Quick Actions
            </h4>
          </div>
          <div className="cov-quick-actions">
            <button className="cov-quick-btn">
              <span className="cov-quick-icon">
                <ClipboardList size={18} />
              </span>
              Create Order
            </button>
            <button className="cov-quick-btn">
              <span className="cov-quick-icon">
                <FileText size={16} />
              </span>
              Create Invoice
            </button>
            <button className="cov-quick-btn">
              <span className="cov-quick-icon">
                <MessageSquarePlus size={16} />
              </span>
              Send Message
            </button>
            <button className="cov-quick-btn">
              <span className="cov-quick-icon">
                <AlertTriangle size={16} />
              </span>
              Create Request
            </button>
            <button className="cov-quick-btn">
              <span className="cov-quick-icon">
                <StickyNote size={16} />
              </span>
              Add Note
            </button>
            <button className="cov-quick-btn">
              <span className="cov-quick-icon">
                <MapPin size={16} />
              </span>
              Add Address
            </button>
            <button className="cov-quick-btn">
              <span className="cov-quick-icon">
                <Car size={16} />
              </span>
              Add Vehicle
            </button>
            <button className="cov-quick-btn">
              <span className="cov-quick-icon">
                <Plus size={16} />
              </span>
              More Actions
            </button>
          </div>
        </div>

        <div className="cov-card">
          <div className="cov-card-header">
            <h4>
              <Gauge size={15} /> Client Health <StaticDot />
            </h4>
          </div>
          <div className="cov-health">
            <div
              className="cov-health-gauge"
              style={{ "--health-pct": `${healthScore}%` }}
            >
              <span>{healthScore}%</span>
            </div>
            <div>
              <p className="cov-health-label">{healthLabel}</p>
              <p className="cov-health-sub">Healthy & Engaged Client</p>
            </div>
          </div>
          <div className="cov-health-list">
            <div className="cov-kv">
              <span>Engagement</span>
              <b className={isRecentlyActive ? "cov-green" : ""}>
                {isRecentlyActive ? "High" : "Medium"}
              </b>
            </div>
            <div className="cov-kv">
              <span>Satisfaction</span>
              <b>
                {client.average_rating > 0
                  ? `${client.average_rating} / 5`
                  : "No ratings yet"}
              </b>
            </div>
            <div className="cov-kv">
              <span>Loyalty</span>
              <b>{client.points?.tier || "—"}</b>
            </div>
            <div className="cov-kv">
              <span>Risk Level</span>
              <b className={`cov-risk-${riskLevel}`}>{RISK_LABEL[riskLevel]}</b>
            </div>
          </div>
        </div>
        <div className="cov-card">
          <div className="cov-card-header cov-card-header--split">
            <h4>
              <Activity size={15} /> Activity Timeline <StaticDot />
            </h4>
            <button type="button" className="cov-link-btn">
              View All
            </button>
          </div>

          <div className="cov-timeline">
            {ACTIVITY_ITEMS.map(
              ({ id, icon: Icon, tone, title, sub, time }) => (
                <div className="cov-timeline-item" key={id}>
                  <span className={`cov-timeline-icon cov-tone-${tone}`}>
                    <Icon size={14} />
                  </span>
                  <div className="cov-timeline-body">
                    <p className="cov-timeline-title">{title}</p>
                    {sub && <p className="cov-timeline-sub">{sub}</p>}
                    {time && <p className="cov-timeline-time">{time}</p>}
                  </div>
                </div>
              ),
            )}
          </div>

          <button type="button" className="cov-timeline-cta">
            View Full Timeline
          </button>
        </div>
      </div>
    </div>
  );
}
